<script setup>
import {
  computed,
  useRoute,
  useHead,
  useSanity,
  useAsyncData,
  showError,
  ref,
  nextTick,
} from '#imports';
import { getQuery as getTheme } from '~/groq-queries/themes';
import ArticlesSearch from '~/components/articles-search/articles-search.vue';

useHead({
  meta: [{ name: 'robots', content: 'noindex' }],
});

const route = useRoute();
const { slug } = route.params;
const { previewId } = route.query;

const isPreview = computed(() => !!previewId);
const sanityClient = computed(() => (previewId ? 'previewClient' : 'default'));

const theme = getTheme({ slug, previewId });

const sanity = useSanity();
const { data: sectionData, refresh } = await useAsyncData('theme', () =>
  sanity.fetch(
    theme,
    {},
    {
      client: sanityClient.value,
    }
  )
);

const themeId = computed(() => sectionData.value._id);
function getImageAssetId(image) {
  return image.asset._ref;
}

function getImageCaption(image) {
  return `Изображение-${getImageAssetId(image)}`;
}

const isDataEmpty = computed(() => !sectionData.value._id);

if (isDataEmpty.value)
  showError({ statusCode: 404, message: 'Страница не найдена' });

const slugToBackgroundColorMap = {
  'what-is-happening':
    'linear-gradient( 180deg, #ffbadf 0, #ffbadf 40%, #fff6fa 100%)',
  stories: 'linear-gradient( 180deg, #FFD2BF 0, #FFD2BF 40%, #fff6fa 100% )',
  guides: 'linear-gradient( 180deg, #e2bdff 0, #e2bdff 40%, #fff6fa 100% )',
  'in-spare-time':
    'linear-gradient( 180deg, #F8E399 0, #F8E399 40%, #fff6fa 100% )',
};
const backgroundColor = computed(() => ({
  '--page-background-color': slugToBackgroundColorMap[slug] || '#F8E399',
}));
const renderComponent = ref(true);

const forceRerender = async () => {
  // Remove MyComponent from the DOM
  renderComponent.value = false;
  // Wait for the change to get flushed to the DOM
  await nextTick();
  await refresh();
  // Add the component back in
  renderComponent.value = true;
};
</script>

<template>
  <LayoutDefaultPage is-header-shown :style="backgroundColor">
    <div v-if="!isDataEmpty" :style="backgroundColor" class="theme">
      <PreviewBar
        v-if="isPreview"
        class="theme-preview__bar"
        @click="forceRerender"
      />

      <div class="theme__content">
        <div
          v-if="sectionData.titleImage || sectionData.titleImageMobile"
          class="theme__title"
        >
          <SanityImage
            v-if="sectionData.titleImage"
            class="theme__title-image"
            :asset-id="getImageAssetId(sectionData.titleImage)"
            :alt="getImageCaption(sectionData.titleImage)"
            auto="format"
          />
          <SanityImage
            v-if="sectionData.titleImageMobile"
            class="theme__title-image theme__title-image--mobile"
            :asset-id="getImageAssetId(sectionData.titleImageMobile)"
            :alt="getImageCaption(sectionData.titleImageMobile)"
            auto="format"
          />
        </div>

        <div v-if="sectionData.description" class="theme__description">
          <PostContent :content="sectionData.description" />
        </div>

        <ArticlesSearch v-if="sectionData" :theme-id="themeId" />
      </div>
    </div>
  </LayoutDefaultPage>
</template>

<style lang="postcss">
.theme {
  --page-background-color: '#F8E399';

  min-height: 100vh;
  padding: 0 60px 60px;
  background: var(--page-background-color);

  &__content {
    max-width: 825px;
    margin: 0 auto;
  }

  &__bar {
    position: sticky;
    z-index: var(--modal-z-index);
    top: 0;
    margin: 0 -60px 40px;
  }

  & .section {
    max-width: 1200px;
  }

  @media (--small-vp) {
    padding: 0 12px 12px;

    &__bar {
      margin: 0 -12px 20px;
    }
  }

  &__title {
    padding: 40px 0 20px;
    max-width: var(--content-max-width);
  }

  &__title-image {
    width: 100%;
    height: auto;
    margin-bottom: 16px;

    &--mobile {
      display: none;
    }
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  @media (--tablet-vp) {
    &__grid {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  @media (--large-vp) {
    &__grid {
      gap: 20px;
    }
  }

  @media (--small-vp) {
    &__title-image {
      display: none;

      &--mobile {
        display: block;
        margin-bottom: 8px;
      }
    }

    &__grid {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }
  }
}
</style>
